import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/pagination';

import {
  Box,
  type BoxProps,
  Stack,
  type SxProps,
  useTheme,
} from '@mui/material';
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { A11y, Pagination } from 'swiper/modules';
import { Swiper, type SwiperProps, type SwiperRef } from 'swiper/react';

import { OffsetCards } from './swiper-offset-module';

export interface SwiperWrapperProps {
  swiper?: SwiperProps;
  swiperContainerSx?: SxProps;
  slotProps?: {
    container?: BoxProps;
  };
  offsetCards?: boolean;
}

export const SwiperWrapper = (props: PropsWithChildren<SwiperWrapperProps>) => {
  const theme = useTheme();
  const swiperRef = useRef<SwiperRef>(null);

  useEffect(() => {
    if (props.swiper?.initialSlide === undefined || !swiperRef.current) {
      return;
    }

    if (props.swiper.initialSlide === swiperRef.current?.swiper?.activeIndex) {
      return;
    }

    // Programmatically change active slide to initial slide if initial slide index
    // changes after the component has already mounted.
    swiperRef.current.swiper.slideTo(props.swiper.initialSlide, 0);
  }, [props.swiper?.initialSlide, swiperRef.current?.swiper]);

  const swiperModules = useMemo(() => {
    return props.offsetCards
      ? [A11y, Pagination, OffsetCards]
      : [A11y, Pagination];
  }, [props.offsetCards]);

  const swiperEffect = useMemo(
    () => (props.offsetCards ? 'offsetCards' : undefined),
    [props.offsetCards],
  );

  return (
    <Stack
      sx={{
        '& .swiper-pagination': {
          position: 'relative',
          top: 0,
          pt: theme.spacing(2),
        },
        // Swiper hides the pagination entirely (display: none) when there's
        // only one slide, collapsing the space it normally reserves below
        // the slide. Cards rely on that space to display their box shadow
        // without it being clipped by the carousel container, so keep the
        // layout space reserved (just visually hidden) even when locked.
        '& .swiper-pagination-lock': {
          display: 'block',
          visibility: 'hidden',
          pointerEvents: 'none',
        },
        '& .swiper-pagination-bullet': {
          background: theme.palette.divider,
          cursor: 'pointer',
          width: '0.55rem',
          height: '0.55rem',
          opacity: 1,

          '&:hover': {
            background: theme.palette.primary.dark,
          },
        },

        '& .swiper-pagination-bullet-active': {
          background: theme.palette.primary.main,
        },
      }}
    >
      <Box
        {...props.slotProps?.container}
        sx={{
          '& .swiper-slide': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '320px',
          },
          ...props.slotProps?.container?.sx,
        }}
      >
        <Swiper
          ref={swiperRef}
          modules={swiperModules}
          spaceBetween={theme.spacing(2)}
          slidesPerView="auto"
          centeredSlides
          effect={swiperEffect}
          pagination={{
            type: 'bullets',
            bulletElement: 'button',
            bulletClass: 'swiper-pagination-bullet',
            bulletActiveClass: 'swiper-pagination-bullet-active',
            clickable: true,
            dynamicBullets: true,
          }}
          onSwiper={(swiper) => {
            if (!props.offsetCards) {
              return;
            }

            (swiper.params as any).offsetCards.spacing = theme.spacing(1);
          }}
          a11y={{
            enabled: true,
            scrollOnFocus: true,
          }}
          {...props.swiper}
        >
          {props.children}
        </Swiper>
      </Box>
    </Stack>
  );
};
